import React, { useState, useEffect } from "react";
import API from "../../API";
import CustomList from "../components/List";
import ListItem from "../components/ListItem";
import ListItemActions from "../components/ListItemActions";
import UsersForm from "./UsersForm";
import CustomPagination from "../components/CustomPagination";
import { Drawer, Col, Modal, Form, Button, Tag, message, Select, Space, Tooltip } from "antd";
import { notifyError, notifySuccess } from "../../Util";
import Loader from "../Packages/componets/Loader";
import { capitalizeWords } from "../../Util";
import { useHistory } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import axios from "../../API/config";

const name = { xxl: 3, xl: 3, lg: 3, md: 3, sm: 8, xs: 16 };
const title = { xxl: 4, xl: 4, lg: 4, md: 4, sm: 8, xs: 0 };
const email = { xxl: 4, xl: 4, lg: 4, md: 4, sm: 0, xs: 0 };
const role = { xxl: 4, xl: 4, lg: 4, md: 4, sm: 0, xs: 0 };
const status = { xxl: 4, xl: 4, lg: 4, md: 4, sm: 0, xs: 0 };

const headerLayout = [
  { ...name, header: "Name" },
  { ...title, header: "Title" },
  { ...email, header: "Email" },
  { ...role, header: "Role" },
  { ...status, header: "Status" },
];
const { confirm } = Modal;

const UsersList = ({ match }) => {
  const [users, setUsers] = useState([]);
  const [visible, setVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editableUser, setEditableUser] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [filters, setFilters] = useState({});
  const [projects, setProjects] = useState([]);
  const [councils, setCouncils] = useState([]);
  const [resendingUserId, setResendingUserId] = useState(null);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1
  });
  const history = useHistory();
  const { user: authUser } = useAuth();

  const [form] = Form.useForm();

  const showDrawer = () => {
    setVisible(true);
  };

  const onClose = () => {
    setVisible(false);
    setEditableUser(null);
    if (form) {
      form.resetFields();
    }
    setIsEdit(false);
  };

  const editUser = (userToEdit) => {
    setEditableUser(userToEdit);
    setIsEdit(true);
    showDrawer();
  };

  const createUser = async (user) => {
    if (user.council_id) {
      user.councilIds = [user.council_id];
    }

    if (user.project) {
      user.projectIds = user.project;
    }

    const payload = {
      ...user,
      procuring_entity_id: authUser?.procuringEntity?.id,
    };

    try {
      setIsSubmitting(true);
      const response = isEdit
        ? await axios.put(`/users/${editableUser.id}`, payload)
        : await axios.post(`/users`, payload);

      onClose();

      const responseMessage = response?.data?.message;

      if (isEdit) {
        notifySuccess(responseMessage || "User updated successfully");
        setIsEdit(false);
      } else if (response?.status === 202) {
        message.warning(responseMessage || "User created but the invitation email could not be delivered. You can resend it from the user list.");
      } else {
        notifySuccess(responseMessage || "User created and invitation sent successfully!");
      }

      getUsers();
    } catch (err) {
      const defaultMessage = isEdit ? "Error updating user" : "Error creating user";
      const apiMessage = err?.response?.data?.message;
      notifyError(apiMessage || defaultMessage);

      const serverDetail = err?.response?.data?.data?.error || err?.response?.data?.data?.message;
      if (serverDetail) {
        message.error(serverDetail);
      }

      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resendInvite = (userId) => {
    setResendingUserId(userId);
    API.resendInvite(userId)
      .then((response) => {
        message.success(response?.message || "Invitation resent successfully!");
        getUsers();
      })
      .catch((err) => {
        const errorMessage = err?.response?.data?.message || "Failed to resend invitation";
        const detail = err?.response?.data?.data?.error;
        message.error(errorMessage);
        if (detail) {
          message.error(detail);
        }
        console.error(err);
      })
      .finally(() => {
        setResendingUserId(null);
      });
  };

  const getInvitationStatusTag = (user) => {
    const status = user.invitation_status;
    switch (status) {
      case 'pending':
        return <Tag color="orange">Invite Pending</Tag>;
      case 'send_failed':
        return (
          <Tooltip title={user.last_invitation_error_message || 'Invitation email failed to send'}>
            <Tag color="red">Invite Failed</Tag>
          </Tooltip>
        );
      case 'accepted':
        return <Tag color="green">Active</Tag>;
      case 'direct':
        return <Tag color="blue">Active</Tag>;
      default:
        return <Tag color="blue">Active</Tag>; // Default to Active for legacy users
    }
  };

  const shouldShowResendInvite = (user) => {
    return ['pending', 'send_failed'].includes(user.invitation_status);
  };

  const getUsers = (page = pagination.current_page, perPage = pagination.per_page) => {
    setIsLoading(true);
    API.getUsers({ ...filters, page, per_page: perPage })
      .then(({ data }) => {
        setIsLoading(false);
        // Handle both direct data array and paginated response structure
        if (data.data && Array.isArray(data.data)) {
          setUsers(data.data);
          if (data.meta) {
            setPagination({
              current_page: data.meta.current_page || page,
              per_page: data.meta.per_page || perPage,
              total: data.meta.total || 0,
              last_page: data.meta.total_pages || 1
            });
          }
        } else if (Array.isArray(data)) {
          setUsers(data);
        } else {
          setUsers([]);
        }
      })
      .catch((err) => {
        setIsLoading(false);
        notifyError("Failed to load users");
        console.error(err);
      });
  };

  const loadProjects = () => {
    API.getPackageProjects()
      .then(({ data }) => {
        setProjects(data.data || []);
      })
      .catch((err) => {
        console.error("Failed to load projects", err);
      });
  };

  const loadCouncils = () => {
    API.getCouncils()
      .then(({ data }) => {
        setCouncils(data || []);
      })
      .catch((err) => {
        console.error("Failed to load councils", err);
      });
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    if (!value) {
      delete newFilters[key];
    }
    setFilters(newFilters);
  };

  const clearFilters = () => {
    setFilters({});
  };

  const handlePageChange = (page, pageSize) => {
    setPagination(prev => ({ ...prev, current_page: page, per_page: pageSize }));
    getUsers(page, pageSize);
  };

  useEffect(() => {
    const currentPage = 1;
    const perPage = pagination.per_page || 15;
    setPagination(prev => ({ ...prev, current_page: currentPage }));
    getUsers(currentPage, perPage);
  }, [filters]);

  useEffect(() => {
    loadProjects();
    loadCouncils();
  }, []);

  const deleteUser = (id) => {
    API.deleteData(`users/${id}`)
      .then(() => {
        getUsers();
        notifySuccess("User archived successfully");
      })
      .catch((err) => {
        notifyError("Error archiving user");
        console.error(err);
      });
  };

  const showArchiveConfirm = (item) => {
    confirm({
      title: `Are you sure you want to archive user ${item.first_name}?`,
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      zIndex: 9999,
      onOk() {
        deleteUser(item.id);
      },
    });
  };

  if (isLoading) {
    return <Loader flexDirection="column" fontSize={54} />;
  }

  return (
    <div style={{ padding: "16px"}}>
      <CustomList
        datatestid="users-list"
        itemName="User"
        backTo="/settings"
        actionButtonProp={{
          title: "Users",
          arrActions: [
            {
              datatestid: "add-user-button",
              btnName: "Add New User",
              btnAction: showDrawer,
            },
          ],
        }}
        items={users}
        page={pagination.current_page}
        itemCount={pagination.total}
        loading={isLoading}
        headerLayout={headerLayout}
        renderBeforeList={() => (
          <div style={{ marginBottom: 16, padding: 16, background: '#fafafa', borderRadius: 6 }}>
            <Space wrap>
              <Select
                placeholder="Filter by Status"
                style={{ width: 150 }}
                allowClear
                value={filters.invite_status}
                onChange={(value) => handleFilterChange('invite_status', value)}
              >
                <Select.Option value="pending">Invite Pending</Select.Option>
                <Select.Option value="accepted">Accepted/Active</Select.Option>
                <Select.Option value="send_failed">Invite Failed</Select.Option>
                <Select.Option value="password_set">Password Set</Select.Option>
              </Select>
              <Select
                placeholder="Filter by Project"
                style={{ width: 200 }}
                allowClear
                showSearch
                value={filters.project_id}
                onChange={(value) => handleFilterChange('project_id', value)}
                optionFilterProp="children"
              >
                {projects.map(project => (
                  <Select.Option key={project.id} value={project.id}>
                    {project.name}
                  </Select.Option>
                ))}
              </Select>
              <Select
                placeholder="Filter by Council/LGA"
                style={{ width: 200 }}
                allowClear
                showSearch
                value={filters.council_id}
                onChange={(value) => handleFilterChange('council_id', value)}
                optionFilterProp="children"
              >
                {councils.map(council => (
                  <Select.Option key={council.id} value={council.id}>
                    {council.name}
                  </Select.Option>
                ))}
              </Select>
              {Object.keys(filters).length > 0 && (
                <Button onClick={clearFilters}>Clear Filters</Button>
              )}
            </Space>
          </div>
        )}
        renderListItem={({ item }) => (
          <ListItem
            key={item.id}
            name={item?.first_name}
            item={item}
            renderActions={() => (
              <ListItemActions
                edit={{
                  name: "Edit User",
                  title: "Edit User",
                  onClick: () => editUser(item),
                }}
                archive={{
                  name: "Archive User",
                  title: "Archive User",
                  onClick: () => showArchiveConfirm(item),
                }}
                customActions={shouldShowResendInvite(item) ? [
                  {
                    name: "Resend Invite",
                    title: "Resend Invitation",
                    onClick: () => resendInvite(item.id),
                    icon: "mail"
                  }
                ] : []}
              />
            )}
          >
            <Col
              {...name}
              className="contentEllipse"
              title={item?.first_name || "-"}
              style={{ cursor: 'pointer' }}
              onClick={() => history.push(`/users/${item.id}`)}
            >
              <span style={{ color: '#1890ff', textDecoration: 'underline' }}>
                {item?.first_name || "-"}
              </span>
            </Col>
            <Col {...title} className="contentEllipse">
              {item?.title || "-"}
            </Col>
            <Col
              {...email}
              className="contentEllipse"
            >
              {item.email}
            </Col>
            <Col {...role} className="contentEllipse">
              {capitalizeWords((item?.roles || []).map(({ name }) => name).join(", ")) || "-"}
            </Col>
            <Col
              {...status}
              className="contentEllipse"
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {getInvitationStatusTag(item)}
                {shouldShowResendInvite(item) && (
                  <Button 
                    type="link" 
                    size="small"
                    loading={resendingUserId === item.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      resendInvite(item.id);
                    }}
                    style={{ padding: 0, height: 'auto' }}
                  >
                    Resend
                  </Button>
                )}
              </div>
            </Col>
          </ListItem>
        )}
      />
      {pagination && pagination.total > pagination.per_page && (
        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
          <CustomPagination 
            pagination={pagination}
            onPageChange={handlePageChange}
            loading={isLoading}
          />
        </div>
      )}
      <Drawer
        title={isEdit ? "Edit User" : "Add New User"}
        placement="right"
        onClose={onClose}
        visible={visible}
        maskClosable={false}
        width={500}
      >
        <UsersForm
          onFinish={createUser}
          onCancel={onClose}
          editableUser={editableUser}
          form={form}
          isSubmitting={isSubmitting}
        />
      </Drawer>
    </div>
  );
};

export default UsersList;
